import config from "appConfig";

const theory = `One speaks of dispersion when the velocity of the wave depends on the frequency. By extension, the dispersion equation is the characteristic equation from which the wave number is deduced and the dispersion diagram is the graph showing the real part of the wave number as a function of the frequency (or the reverse: it is called Brillouin diagram).
<h3 class=sectionHead>Phase velocity and group velocity</h3>
<p>
   Two definitions are possible to define the velocity of the wave:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>k</mi>
         </mrow>
      </mfrac>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>∂ω</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>∂k</mi>
         </mrow>
      </mfrac>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>Re</mi>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>
   the real part of the complex wave number
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>. These two definitions have different physical sense and we show on a particular case what these two velocities are.
<p>Consider two pure waves of neighboring frequency and same amplitude propagating in the same direction in a lossless dispersive medium (see <a href="#" data-img-src=${config.ASSETS_URL}/01/25/theory/figure-1.svg data-img-alt="Figure 1. Propagation of a bichromatic wave in a dispersive medium at two different moments in time. The left arrow shows the shift of the maximum of the envelope (group velocity <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>c</mi></mrow><mrow><mi>g</mi></mrow></msub></math> ). The right arrow represents the shift of the local maximum (phase velocity <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>c</mi></mrow><mrow><mi>ϕ</mi></mrow></msub></math> )">Figure 1</a>). One has
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mi>A</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>which can be rewritten as
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mi>A</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mi>t</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mi>x</mi>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mi>t</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <msub>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mi>x</mi>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   Setting
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
   </math>
   the mean angular frequency,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
   </math>
   the mean wave number,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Δω</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mo class=MathClass-rel>|</mo>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>|</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Δk</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mo class=MathClass-rel>|</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>|</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
   </math>, leads to
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mi>A</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
                  <mi>t</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mover accent=true>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
                  <mi>x</mi>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mi mathvariant=italic>Δω</mi>
                  <mi>t</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>Δk</mi>
                  <mi>x</mi>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(3)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 1. Propagation of a bichromatic wave in a dispersive medium at two different moments in time. The left arrow shows the shift of the maximum of the envelope (group velocity <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>c</mi></mrow><mrow><mi>g</mi></mrow></msub></math> ). The right arrow represents the shift of the local maximum (phase velocity <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>c</mi></mrow><mrow><mi>ϕ</mi></mrow></msub></math> )." src=${config.ASSETS_URL}/01/25/theory/figure-1.svg>
      <figcaption class=caption>
         Figure 1. Propagation of a bichromatic wave in a dispersive medium at two different moments in time. The left arrow shows the shift of the maximum of the envelope (group velocity
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi>g</mi>
               </mrow>
            </msub>
         </math>). The right arrow represents the shift of the local maximum (phase velocity
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
            </msub>
         </math>).
      </figcaption>
</figure>
<p>
   It appears that a wave of angular frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   propagates with the velocity
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>This wave is modulated by an envelope propagating with the velocity
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi>g</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>Δω</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>Δk</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>≈</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂ω</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂k</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(5)
</table>
<p>The velocity of the envelope is therefore the velocity of propagation of the energy, called group velocity, and the velocity of the wave inside the envelope is called the phase velocity.
<h3 class=sectionHead>Klein-Gordon type dispersion equation</h3>
<p>In some situations waves propagate only above a given frequency. This is the case, for example, of higher modes in a waveguide (see lesson 1.18) or for the propagation in an exponential horn (see lesson 1.30). This situation is well described by the following relationship where the wave number is given by
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>k</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mi>∞</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>−</mo>
                  <msubsup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
            </msqrt>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>∞</mi>
         </mrow>
      </msub>
   </math>
   is the limit velocity when the frequency approaches infinity and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   the cut-off angular frequency. This is called a Klein-Gordon dispersion type (see <a href="#" data-img-src=${config.ASSETS_URL}/01/25/theory/figure-2.svg data-img-alt="Figure 2. Klein Gordon type dispersion diagram with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mfrac><mrow><msub><mrow><mi>ω</mi></mrow><mrow><mi>c</mi></mrow></msub></mrow><mrow><msub><mrow><mi>c</mi></mrow><mrow><mi>∞</mi></mrow></msub></mrow></mfrac><mo class=MathClass-rel>=</mo><mn>1</mn></math>.">Figure 2</a>).
   <figure class=figure>
<p><img alt="Figure 2. Klein Gordon type dispersion diagram with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mfrac><mrow><msub><mrow><mi>ω</mi></mrow><mrow><mi>c</mi></mrow></msub></mrow><mrow><msub><mrow><mi>c</mi></mrow><mrow><mi>∞</mi></mrow></msub></mrow></mfrac><mo class=MathClass-rel>=</mo><mn>1</mn></math>." src=${config.ASSETS_URL}/01/25/theory/figure-2.svg><figcaption class=caption>Figure 2. Klein Gordon type dispersion diagram with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mfrac><mrow><msub><mrow><mi>ω</mi></mrow><mrow><mi>c</mi></mrow></msub></mrow><mrow><msub><mrow><mi>c</mi></mrow><mrow><mi>∞</mi></mrow></msub></mrow></mfrac><mo class=MathClass-rel>=</mo><mn>1</mn></math>.</figcaption></figure>
<p>
   Indeed, if
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
   </math>
   is purely imaginary and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jkx</mi>
         </mrow>
      </msup>
   </math> is a real decreasing exponential: there is no propagation. Waves are said evanescent.
<p>
   Real waves (i.e. propagative waves) appear only above the cutoff frequency when the wave number is real. In this case we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi>∞</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <msubsup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>∞</mi>
         </mrow>
      </msub>
      <msqrt>
         <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <msubsup>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-bin>∕</mo>
            <msup>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </msqrt>
   </math>. Then
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>∞</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>∞</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
   </math>. In particular, at cutoff
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>∞</mi>
   </math>.
<h3 class=sectionHead>Dispersion in a tube with visco-thermal losses</h3>
<p>
   In this case, dispersion is low. We have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-bin>+</mo>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mi>j</mi>
      <mo class=MathClass-close>)</mo>
      <mi>α</mi>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>
   is the speed of sound in free air and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
      <mo class=MathClass-rel>≈</mo>
      <mn>3</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>0</mn>
      <mo class=MathClass-bin>⋅</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>5</mn>
         </mrow>
      </msup>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>. Considering that
   <strong>
      <mstyle class=text>
         <mtext>Re</mtext>
      </mstyle>
   </strong>
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mo>(</mo>
   <msub>
      <mrow>
        <mi>k</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
      </mrow>
   </msub>
   <mo>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mrow>
   <mfrac>
      <mrow>
         <mi>ω</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
      </mrow>
   </mfrac>
    </mrow>
   <mo class=MathClass-bin>+</mo>
   <mi>α</mi>
    </math>. It can be deduced that
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>αc</mi>
                     </mrow>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
            <mstyle class=text>
               <mtext>and</mtext>
            </mstyle>
            <msub>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi>g</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msup>
               <mrow>
                  <mrow>
                     <mo form=prefix fence=true>(</mo>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <mi>∂</mi>
                              <mo class=MathClass-open>(</mo>
                              <mi>ω</mi>
                              <mo class=MathClass-bin>∕</mo>
                              <mi>c</mi>
                              <mo class=MathClass-bin>+</mo>
                              <mi>α</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo form=postfix fence=true>)</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>αc</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                        <mi>ω</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   Unlike the previous case, the two velocities are lower than the speed of sound in free air and obviously
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≠</mo>
      <msup>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>. The group velocity decreases when frequency decreases. However, the effect is rather limited: for example in a tube of radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math> cm at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math> Hz the group velocity is only
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
      <mi>%</mi>
   </math>
   lower than the speed of sound in free air.
   <h3 class=sectionHead>References</h3>
<dl class=thebibliography>
  <dt>[1]
  <dd>L. Brillouin. Wave propagation in periodic structures. MC Graw Hill, 1946.
  <dt>[2]
  <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
  <dt>[3]
  <dd>E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
  <dt>[4]
  <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
